import { ExtractedRecord } from "@/types";

import { claimantDedupKey } from "./recordUtils";

/**
 * Find all records linked to the given record, within `records`.
 *
 * For claimant records: every record (any schema) sharing the GRID'S OWN
 * DEDUPE KEY (`claimantDedupKey`: member_id, falling back to claimant_name plus
 * the household segments, #7578) — INCLUDING the record itself. The Claimants
 * tab is deduped by that key, so multiple raw rows for one claimant collapse to
 * one grid row; expanding it must surface every original row's claim_item
 * content (the parent's own data plus any deduped-away siblings'). Excluding
 * self would leave the parent's claim_items unrendered.
 *
 * Matching on claimant_name alone was a wider net than the dedupe it
 * compensated for: on a file where names repeat it listed OTHER people's claim
 * lines under one claimant, while the YTD beside them — computed on
 * name + DOB + member_id — correctly covered only that member (measured:
 * 50 distinct names across 497,710 members, every name shared, up to 9,955
 * rows under one name). It also contradicted #5766: two same-name members
 * with different member_ids must NOT be treated as one claimant. (#6830)
 *
 * For non-claimant records: fall back to matching by shared
 * line_item_row_index, excluding the record itself.
 *
 * `records` is whatever the caller has LOADED (#6755). On a paged document a
 * linked record on an unfetched page will not appear here — making this
 * whole-set needs server-side linkage, tracked as #6848; the alternative
 * (fetching everything to compute it) is the request that story removed.
 * Until then the bulk-exclude toolbar disclosure (RecordReviewGrid) states
 * the scope, the same way search does.
 *
 * Exported as a pure function so tests pin THIS predicate — the one the
 * review surface actually cascades and expands with — rather than a
 * reimplementation that can drift from it (#6830 AC1).
 */
export const getLinkedRecords = (record: ExtractedRecord, records: ExtractedRecord[]): ExtractedRecord[] => {
  if (!records.length) return [];

  const key = claimantDedupKey(record);
  if (key !== null) {
    return records.filter((r) => claimantDedupKey(r) === key);
  }

  // Excludes undefined as well as null: the previous `!== null` alone kept `undefined` in the set,
  // so a keyless record whose fields carried NO row info "shared" the
  // undefined index with every other such record and linked to all of them —
  // surfaced when these tests moved from a reimplemented predicate onto this
  // real one (#6755 review). No row index means no linkage, full stop.
  const rowIndices = new Set(
    (record.extracted_fields ?? [])
      .map((f) => f.source?.line_item_row_index)
      .filter((i): i is number => i !== null && i !== undefined)
  );
  if (rowIndices.size === 0) return [];
  return records.filter(
    (r) =>
      r !== record &&
      (r.extracted_fields ?? []).some(
        (f) =>
          f.source?.line_item_row_index !== null &&
          f.source?.line_item_row_index !== undefined &&
          rowIndices.has(f.source.line_item_row_index)
      )
  );
};
